import { type ClassValue, clsx } from "clsx";
import type React from "react";
import { twMerge } from "tailwind-merge";

// Utility function for classnames
function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// Typography types and tokens
export type TextSize =
	| "xs"
	| "sm"
	| "base"
	| "lg"
	| "xl"
	| "2xl"
	| "3xl"
	| "4xl"
	| "5xl";

export type TextWeight = "light" | "normal" | "medium" | "semibold" | "bold";

export type TextFont = "sans" | "serif";

export type TextVariant =
	| "display"
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "body-lg"
	| "body"
	| "body-sm"
	| "caption"
	| "pull-quote";

export interface TextStyleProps {
	size?: TextSize;
	weight?: TextWeight;
	font?: TextFont;
	leading?: "none" | "tight" | "snug" | "normal" | "relaxed" | "loose";
	tracking?: "tighter" | "tight" | "normal" | "wide" | "wider";
}

// Typography size tokens
const textSizeClasses: Record<TextSize, string> = {
	xs: "text-xs", // 0.75rem (12px)
	sm: "text-sm", // 0.875rem (14px)
	base: "text-base", // 1rem (16px)
	lg: "text-lg", // 1.125rem (18px)
	xl: "text-xl", // 1.25rem (20px)
	"2xl": "text-2xl", // 1.5rem (24px)
	"3xl": "text-3xl", // 1.875rem (30px)
	"4xl": "text-4xl", // 2.25rem (36px)
	"5xl": "text-5xl", // 3rem (48px)
};

// Typography weight tokens
const textWeightClasses: Record<TextWeight, string> = {
	light: "font-light", // 300
	normal: "font-normal", // 400
	medium: "font-medium", // 500
	semibold: "font-semibold", // 600
	bold: "font-bold", // 700
};

// Typography font tokens
const textFontClasses: Record<TextFont, string> = {
	sans: "font-sans",
	serif: "font-serif",
};

// Line height tokens
const textLineHeightClasses: Record<
	"none" | "tight" | "snug" | "normal" | "relaxed" | "loose",
	string
> = {
	none: "leading-none", // 1
	tight: "leading-tight", // 1.25
	snug: "leading-snug", // 1.375
	normal: "leading-normal", // 1.5
	relaxed: "leading-relaxed", // 1.625
	loose: "leading-loose", // 2
};

// Letter spacing tokens
const textLetterSpacingClasses: Record<
	"tighter" | "tight" | "normal" | "wide" | "wider",
	string
> = {
	tighter: "tracking-tighter", // -0.05em
	tight: "tracking-tight", // -0.025em
	normal: "tracking-normal", // 0
	wide: "tracking-wide", // 0.025em
	wider: "tracking-wider", // 0.05em
};

// Typography variant definitions
const textVariantClasses: Record<TextVariant, TextStyleProps> = {
	display: {
		size: "5xl",
		weight: "bold",
		leading: "tight",
	},
	h1: {
		size: "4xl",
		weight: "bold",
		leading: "tight",
	},
	h2: {
		size: "3xl",
		weight: "semibold",
		leading: "tight",
	},
	h3: {
		size: "2xl",
		weight: "semibold",
		leading: "snug",
	},
	h4: {
		size: "xl",
		weight: "medium",
		leading: "snug",
	},
	h5: {
		size: "lg",
		weight: "medium",
		leading: "normal",
	},
	"body-lg": {
		size: "lg",
		weight: "normal",
		leading: "relaxed",
	},
	body: {
		size: "base",
		weight: "normal",
		leading: "relaxed",
	},
	"body-sm": {
		size: "sm",
		weight: "normal",
		leading: "normal",
	},
	caption: {
		size: "xs",
		weight: "normal",
		leading: "normal",
	},
	"pull-quote": {
		size: "2xl",
		weight: "medium",
		font: "serif",
		leading: "relaxed",
	},
};

// Helper to get classes for a variant
function getVariantClasses(
	variant: TextVariant,
	font?: TextFont,
): ClassValue[] {
	const variantStyles = textVariantClasses[variant];
	const classes: ClassValue[] = [
		textSizeClasses[variantStyles.size!],
		textWeightClasses[variantStyles.weight!],
		font
			? textFontClasses[font]
			: variantStyles.font
				? textFontClasses[variantStyles.font]
				: textFontClasses.sans,
	];

	if (variantStyles.leading) {
		classes.push(textLineHeightClasses[variantStyles.leading]);
	}

	if (variantStyles.tracking) {
		classes.push(textLetterSpacingClasses[variantStyles.tracking]);
	}

	return classes;
}

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
	variant?: TextVariant;
	font?: TextFont;
	weight?: TextWeight;
	size?: TextSize;
	leading?: "none" | "tight" | "snug" | "normal" | "relaxed" | "loose";
	tracking?: "tighter" | "tight" | "normal" | "wide" | "wider";
	as?:
		| "h1"
		| "h2"
		| "h3"
		| "h4"
		| "h5"
		| "h6"
		| "p"
		| "span"
		| "div"
		| "blockquote";
	truncate?: boolean;
	className?: string;
	children: React.ReactNode;
}

const semanticElements: Record<TextVariant, React.ElementType> = {
	display: "h1",
	h1: "h1",
	h2: "h2",
	h3: "h3",
	h4: "h4",
	h5: "h5",
	"body-lg": "p",
	body: "p",
	"body-sm": "p",
	caption: "span",
	"pull-quote": "blockquote",
};

export function Text({
	variant = "body",
	font,
	weight,
	size,
	leading,
	tracking,
	as,
	truncate = false,
	className,
	children,
	...props
}: TextProps) {
	// Determine which HTML element to render
	const ElementTag =
		as || ((variant ? semanticElements[variant] : "p") as React.ElementType);

	// Build the classes
	const classes = cn(
		// If variant is provided, use variant styles as base
		variant && getVariantClasses(variant, font),

		// Allow overriding individual properties
		font && textFontClasses[font],
		weight && textWeightClasses[weight],
		size && textSizeClasses[size],
		leading && textLineHeightClasses[leading],
		tracking && textLetterSpacingClasses[tracking],

		// Handle truncation
		truncate && "truncate",

		// Additional classes
		className,
	);

	return (
		<ElementTag className={classes} {...props}>
			{children}
		</ElementTag>
	);
}

// Convenience components for common variants
export function Heading({
	level = 1,
	...props
}: Omit<TextProps, "variant" | "as"> & { level?: 1 | 2 | 3 | 4 | 5 }) {
	const variant = `h${level}` as TextVariant;
	return <Text variant={variant} {...props} />;
}

export function DisplayHeading(props: Omit<TextProps, "variant" | "as">) {
	return <Text variant="display" {...props} />;
}

export function Paragraph(props: Omit<TextProps, "variant" | "as">) {
	return <Text variant="body" {...props} />;
}

export function Caption(props: Omit<TextProps, "variant" | "as">) {
	return <Text variant="caption" {...props} />;
}

export function PullQuote(props: Omit<TextProps, "variant" | "as">) {
	return <Text variant="pull-quote" {...props} />;
}
